import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Popover, { type PopoverOrigin } from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type PropsWithChildren, type ReactNode, type Ref, useState } from 'react';

type DropdownVariant = 'primary' | 'alternative' | 'basic';

type VariantConfig = {
  color: string;
  hoverBackgroundColor: string;
  focusRingColor: string;
  shadowColor: string;
};

const VARIANT_CONFIG: Record<DropdownVariant, VariantConfig> = {
  primary: {
    color: '#6BAA00',
    hoverBackgroundColor: 'rgba(107, 170, 0, 0.1)',
    focusRingColor: 'rgba(107, 170, 0, 0.2)',
    shadowColor: 'rgba(107, 170, 0, 0.14)',
  },
  alternative: {
    color: '#22A7F0',
    hoverBackgroundColor: 'rgba(34, 167, 240, 0.1)',
    focusRingColor: 'rgba(34, 167, 240, 0.2)',
    shadowColor: 'rgba(34, 167, 240, 0.14)',
  },
  basic: {
    color: '#49454f',
    hoverBackgroundColor: 'rgba(73, 69, 79, 0.1)',
    focusRingColor: 'rgba(73, 69, 79, 0.18)',
    shadowColor: 'rgba(73, 69, 79, 0.12)',
  },
};

export type Props = PropsWithChildren<{
  ref?: Ref<HTMLButtonElement>;
  variant: DropdownVariant;
  label?: ReactNode;
  icon?: ReactNode;
  iconPosition?: 'start' | 'end';
  ariaLabel?: string;
  menuAriaLabel?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  tid?: string;
  popupTid?: string;
  closeOnContentClick?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}>;

function DropdownBase(props: Props) {
  const {
    ref,
    variant,
    label,
    icon,
    iconPosition,
    ariaLabel,
    menuAriaLabel,
    children,
    disabled,
    fullWidth,
    tid,
    popupTid,
    closeOnContentClick = false,
    anchorOrigin,
    transformOrigin,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const variantConfig = VARIANT_CONFIG[variant];
  const hasLabel = label !== undefined && label !== null;
  const open = Boolean(anchorEl);

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((current) => (current ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const iconProps =
    icon && hasLabel ? (iconPosition === 'end' ? { endIcon: icon } : { startIcon: icon }) : {};
  const triggerContent = hasLabel ? label : icon;

  return (
    <>
      <MuiButton
        ref={ref}
        variant="outlined"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        fullWidth={fullWidth}
        data-testid={tid}
        sx={{
          minWidth: 0,
          borderRadius: '9999px',
          padding: 0.5,
          border: 'none',
          color: variantConfig.color,
          backgroundColor: open ? variantConfig.hoverBackgroundColor : 'transparent',
          boxShadow: open
            ? `0 0 0 1px ${variantConfig.focusRingColor}, 0 5px 14px ${variantConfig.shadowColor}`
            : 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          textTransform: 'none',
          transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            transition: 'transform 180ms ease',
          },
          '&:hover': {
            border: 'none',
            backgroundColor: variantConfig.hoverBackgroundColor,
            boxShadow: `0 4px 12px ${variantConfig.shadowColor}`,
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
          '&.Mui-focusVisible': {
            boxShadow: `0 0 0 3px ${variantConfig.focusRingColor}`,
          },
          '&.Mui-disabled': {
            border: 'none',
            color: '#a7a7a7',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            WebkitTextFillColor: '#a7a7a7',
          },
        }}
        {...iconProps}
      >
        {triggerContent}
      </MuiButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={
          anchorOrigin ?? {
            vertical: 'bottom',
            horizontal: 'left',
          }
        }
        transformOrigin={
          transformOrigin ?? {
            vertical: 'top',
            horizontal: 'left',
          }
        }
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 'max-content',
              minWidth: 240,
              maxWidth: 'min(560px, calc(100vw - 32px))',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
              border: '1px solid rgba(34, 167, 240, 0.14)',
              borderRadius: '10px',
              boxShadow: '0 18px 44px rgba(47, 67, 25, 0.16), 0 3px 10px rgba(34, 167, 240, 0.08)',
            },
          },
        }}
      >
        <Box
          role="menu"
          aria-label={menuAriaLabel}
          data-testid={popupTid}
          onClick={closeOnContentClick ? handleClose : undefined}
          sx={{ width: '100%', py: 0.75 }}
        >
          {hasLabel ? (
            <Box
              sx={{
                px: 2,
                pt: 0.75,
                pb: 0.625,
              }}
            >
              <Typography
                component="div"
                sx={{
                  color: '#8A8790',
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1.4,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </Typography>
            </Box>
          ) : null}
          {children}
        </Box>
      </Popover>
    </>
  );
}

DropdownBase.displayName = 'SiloReal-Dropdown-Base';

export const base = DropdownBase;
